// Order Api
import { http } from "./http";

/**
 * Order Management APIs
 */

// Fetch all orders
export interface Order {
  order_id: number;
  order_number: string;
  customer_id?: number | null;
  customer_name: string;
  customer_email?: string;
  customer_phone?: string;
  note?: string | null;
  shipping_address?: {
    label?: string;
    full_name?: string;
    phone?: string;
    address?: string;
    city?: string;
    state?: string;
    zip_code?: string;
  } | null;
  direct_shipping_address?: string;
  payment_status: string;
  order_status?: "active" | "cancelled" | string;
  cancelled_at?: string | null;
  cancelled_by?: number | null;
  cancellation_reason?: string | null;
  cancellation_meta?: any;
  payment_method: string;
  is_wholesale?: boolean;
  wholesale_tier_id?: number | null;
  wholesale_tier_name?: string | null;
  payments?: any;
  transactions?: {
    id: number;
    amount: number;
    payment_method: string;
    card_type?: string | null;
    transaction_type?: string | null;
    created_at: string;
  }[];
  subtotal: number;
  discount_type?: "percentage" | "percent" | "fixed" | null;
  discount_value: number;
  loyalty_points_used: number;
  tax: number;
  shipping_cost: number;
  total_price: number;
  fulfillment_id?: number;
  fulfillment_status?: string;
  fulfillment_type?: string;
  fulfillment_method?: string;
  tracking_number?: string | null;
  tracking_url?: string | null;
  carrier?: string | null;

  payment_balance?: {
    paidTotal: number;
    balanceDue: number;
    refundDue: number;
    paymentBalanceStatus: "unpaid" | "balanced" | "balance_due" | "refund_due";
  };

  /** 🆕 Delivery fields */
  is_delivery?: boolean;
  delivery_fee?: number;
  delivery_status?: string;

  created_at: string;
  is_online_order: boolean;
  store_id?: number;
  store_name?: string;

  items: {
    id: number;
    image_url?: string;
    attributes: any[]; // you normalize it anyway
    product_id: number;
    variant_id?: number | null;
    quantity: number;
    price: number;
    product_name: string;
    variant_name?: string | null;
  }[];
}

export const fetchOrderCounts = async (): Promise<{ pending: number; to_fulfill: number }> => {
  const response = await http.get<{ pending: number; to_fulfill: number }>("/orders/counts");
  return response.data;
};

export const fetchOrders = async (params: { source?: string; limit?: number; offset?: number; search?: string; status?: string } = {}): Promise<Order[]> => {
  const response = await http.get<Order[]>("/orders", {
    params, // This sends ?source=online&limit=50&offset=0
  });

  return response.data.map((order) => ({
    ...order,
    subtotal: Number(order.subtotal) || 0,
    discount_value: Number(order.discount_value) || 0,
    loyalty_points_used: Number(order.loyalty_points_used) || 0,
    tax: Number(order.tax) || 0,
    shipping_cost: Number(order.shipping_cost) || 0,
    total_price: Number(order.total_price) || 0,
    payment_status: order.payment_status || "pending",
    payment_method: order.payment_method || "N/A",
    transactions: Array.isArray(order.transactions) ? order.transactions : [],
    shipping_address: order.shipping_address || {
      address: order.direct_shipping_address,
      city: "",
      state: "",
      zip_code: ""
    },
    payment_balance: order.payment_balance || order.payments?.balance || undefined,
  }));
};

// Create a new order
export const createOrder = async (orderData: {
  customer_name: string;
  items: { product_id: number; quantity: number }[];
}) => {
  const response = await http.post("/orders", orderData);
  return response.data;
};

// Update an order's payment status
export const updateOrder = async (orderId: number, updateData: { payment_status: string }) => {
  const response = await http.put(`/orders/${orderId}`, updateData);
  return response.data;
};

export const updateOrderDetails = async (
  orderId: number,
  payload: {
    customer_name: string;
    customer_email: string;
    customer_phone: string;
    shipping_address: string;
    shipping_city: string;
    shipping_state: string;
    shipping_zip_code: string;
    note: string;
  }
) => {
  const response = await http.put(`/orders/${orderId}/details`, payload);
  return response.data;
};

export const confirmEtransferPayment = async (
  orderId: number,
  payload?: { amount_collected?: number }
) => {
  const response = await http.post(
    `/orders/${orderId}/confirm-etransfer-payment`,
    payload || {}
  );
  return response.data;
};


export const recordManualPayment = async (
  orderId: number,
  payload: {
    amount_collected: number;
    payment_method: "cash" | "etransfer" | "manual_card" | "other" | "store_credit";
    note?: string;
  }
) => {
  const response = await http.post(`/orders/${orderId}/record-manual-payment`, payload);
  return response.data;
};

export const resendPaymentLink = async (orderId: number) => {
  const response = await http.post(`/orders/${orderId}/resend-payment-link`);
  return response.data;
};


export const processProviderRefund = async (
  orderId: number,
  payload: {
    amount?: number;
    reason?: string;
  }
) => {
  const response = await http.post(`/orders/${orderId}/refund`, payload);
  return response.data;
};

export const recordManualRefund = async (
  orderId: number,
  payload: {
    amount: number;
    method: string;
    reason?: string;
    note?: string;
  }
) => {
  const response = await http.post(`/orders/${orderId}/refund/manual`, payload);
  return response.data;
};

export const cancelOrder = async (
  orderId: number,
  payload: {
    refund_mode: "none" | "provider" | "manual";
    manual_refund_method?: string;
    reason?: string;
    force?: boolean;
    amount?: number;
  }
) => {
  const response = await http.post(`/orders/${orderId}/cancel`, payload);
  return response.data;
};

// Delete an order
export const deleteOrder = async (orderId: number) => {
  const response = await http.delete(`/orders/${orderId}`);
  return response.data;
};

// Generate receipt
export const generateReceipt = async (orderId: number) => {
  const response = await http.get(`/orders/${orderId}/receipt`);
  return response.data;
};

export const sendReceipt = async (orderId: number) => {
  const response = await http.post(`/pos/orders/${orderId}/send-receipt`);
  return response.data;
};


export const addItemToOrder = async (
  orderId: number,
  payload: {
    product_id: number;
    variant_id?: number | null;
    quantity: number;
    // Optional per-line price override (e.g. 0 for a free replacement line).
    price?: number;
    price_edit_reason?: string;
  }
) => {
  const response = await http.post(`/orders/${orderId}/items`, payload);
  return response.data;
};

export const updateOrderItem = async (
  itemId: number,
  payload: { quantity: number }
) => {
  const response = await http.put(`/orders/items/${itemId}`, payload);
  return response.data;
};

export const removeOrderItem = async (itemId: number) => {
  const response = await http.delete(`/orders/items/${itemId}`);
  return response.data;
};



export interface AdminEditOrderItem {
  id?: number;
  product_id: number;
  variant_id?: number | null;
  quantity: number;
  // Optional manual price override — honoured server-side only while the
  // order is unpaid (nothing collected); ignored otherwise.
  price?: number;
}

export interface AdminEditOrderPayload {
  fulfillment_method?: "shipping" | "local_delivery";
  items: AdminEditOrderItem[];
  customer?: Record<string, any>;
  payment_adjustment?: { amount_collected: number; method?: string } | null;
  price_edit_reason?: string | null;
  // When true the preview reflects price overrides (adjustment mode for paid orders).
  adjust?: boolean;
}

// Settlement instruction for adjusting a PAID order.
export interface OrderAdjustmentSettlement {
  // used when the new total is lower than what was collected
  refund_destination?: "manual" | "store_credit";
  method?: string;
  // used when the new total is higher than what was collected
  collect?: { amount: number; method: string };
}

export interface AdjustPaidOrderPayload {
  fulfillment_method?: "shipping" | "local_delivery";
  items: AdminEditOrderItem[];
  settlement?: OrderAdjustmentSettlement;
  reason?: string | null;
}

export async function adminEditOrder(
  orderId: number,
  payload: AdminEditOrderPayload
) {
  const res = await http.put(`/orders/${orderId}/admin-edit`, payload);
  return res.data;
}

export async function previewAdminEditOrder(
  orderId: number,
  payload: AdminEditOrderPayload
) {
  const res = await http.post(`/orders/${orderId}/admin-edit-preview`, payload);
  return res.data;
}

// Adjust a PAID order: change items/prices, settle the delta (refund to
// original/store credit, or collect more).
export async function adjustPaidOrder(
  orderId: number,
  payload: AdjustPaidOrderPayload
) {
  const res = await http.put(`/orders/${orderId}/adjust`, payload);
  return res.data;
}

export const createAdminPayableOrder = async (payload: {
  customer: {
    name: string;
    email: string;
    phone?: string;
    shipping_address?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    country?: string;
  };
  items: {
    product_id: number;
    variant_id?: number | null;
    quantity: number;
    price?: number;
    // True when the admin hand-edited this line's price (e.g. a free
    // replacement). The backend honors it verbatim; otherwise it resolves the
    // price server-side (wholesale tier/breaks or retail).
    price_overridden?: boolean;
  }[];
  fulfillment_method: "shipping" | "local_delivery";
  payment_method?: "payment_link" | "etransfer" | "clover" | "paypal";
  shipping_cost?: number;
  delivery_fee?: number;
  note?: string;
  create_account?: boolean;
  // A manual discount OR a coupon (mutually exclusive) applied to the order.
  discount?: { type: "percentage" | "fixed"; value: number } | null;
  coupon_code?: string | null;
  // Order channel. Omit/null → auto (wholesale iff the customer is an approved
  // wholesale account). false → force retail for this one order.
  wholesale?: boolean | null;
}) => {
  const response = await http.post("/orders/admin-create-payable", payload);
  return response.data;
};

// Set/replace/clear an existing (unpaid) order's manual discount OR coupon.
export const setOrderDiscount = async (
  orderId: number,
  payload:
    | { discount: { type: "percentage" | "fixed"; value: number } }
    | { coupon_code: string }
    | { clear: true }
) => {
  const res = await http.put(`/orders/${orderId}/discount`, payload);
  return res.data;
};

export type AdminOrderCustomerSearchResult = {
  id: number;
  name: string;
  email: string;
  phone?: string | null;
  is_wholesale?: boolean;
  wholesale_tier_id?: number | null;
  wholesale_tier_name?: string | null;
  wholesale_discount_pct?: number | string | null;
  loyalty_balance?: number | string;
  address_id?: number | null;
  full_name?: string | null;
  address_phone?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  country?: string | null;
};

// Attach an existing customer (customer_id) or create+attach a new one to an
// order — enables store-credit refunds on customer-less orders.
export async function attachCustomerToOrder(
  orderId: number,
  payload:
    | { customer_id: number }
    | { new_customer: { name: string; email: string; phone?: string } }
) {
  const res = await http.put(`/orders/${orderId}/customer`, payload);
  return res.data;
}

export const searchCustomersForAdminOrderCreate = async (
  query: string
): Promise<AdminOrderCustomerSearchResult[]> => {
  const trimmed = query.trim();
  if (!trimmed) return [];

  const response = await http.get("/orders/admin-create-customer-search", {
    params: { query: trimmed },
  });

  return Array.isArray(response.data) ? response.data : [];
};

export const getAdminShippingRate = async () => {
  const response = await http.get("/api/settings/shipping/public-rate", {
    params: { store_id: 1 },
  });

  return response.data;
};

export const getAdminDeliveryQuote = async (payload: {
  city: string;
  subtotal: number;
}) => {
  const response = await http.post("/api/deliveries/quote", payload);;
  return response.data;
};